import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchAllSpeakingExercises,
  updateSpeakingExercise,
  deleteSpeakingExercise,
  generateSpeakingAudio
} from '../../services/api';
import Loading from '../Loading';
import Error from '../Error';
import AdminTable from './AdminTable';
import PlayAudioButton from './PlayAudioButton';
import { grammarCategoriesField } from './GrammarCategoriesField';

const SpeakingExerciseManager = () => {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [audioResult, setAudioResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      const response = await fetchAllSpeakingExercises();
      setExercises(response.exercises || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching speaking exercises:', err);
      setError('Failed to load speaking exercises');
      setExercises([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExercises();
  }, []);

  const uniqueTopics = useMemo(() => {
    return [...new Set(exercises.map((item) => item.topic))].filter(Boolean);
  }, [exercises]);

  const handleSave = async (id, data) => {
    await updateSpeakingExercise(id, data);
    setExercises((prev) => prev.map((ex) => (ex.id === id ? { ...ex, ...data } : ex)));
  };

  const handleDelete = async (id) => {
    await deleteSpeakingExercise(id);
    setExercises((prev) => prev.filter((ex) => ex.id !== id));
  };

  const handleGenerateAudio = async () => {
    if (!window.confirm(`Tato akce vygeneruje audio pro ${selectedIds.length} vybraných otázek. Existující audio bude přepsáno. Pokračovat?`)) {
      return;
    }

    try {
      setGeneratingAudio(true);
      setAudioResult(null);
      const data = await generateSpeakingAudio(selectedIds);
      setAudioResult({
        success: true,
        message: `Audio vygenerováno pro ${data.generated_count} z ${data.total_exercises} otázek.`,
        errors: data.errors
      });
      setSelectedIds([]);
      // Refresh exercises to update has_audio status
      await fetchExercises();
    } catch (err) {
      console.error('Error generating audio:', err);
      setAudioResult({
        success: false,
        message: 'Nepodařilo se vygenerovat audio: ' + err.message
      });
    } finally {
      setGeneratingAudio(false);
    }
  };

  const columns = [
    { key: 'id', header: 'ID', width: 60 },
    { key: 'topic', header: 'Topic', editable: true, width: 120 },
    { key: 'exercise_order', header: 'Order', width: 70 },
    { key: 'question', header: 'Question', editable: true, width: 250 },
    { key: 'hint', header: 'Hint', editable: true, width: 250 },
    { key: 'instruction', header: 'Instruction', editable: true, width: 150 },
    { key: 'grammar_categories', header: 'Grammar Categories', editable: true, width: 150,
      render: (value) => Array.isArray(value) ? value.join(', ') : (value || ''),
    },
    {
      key: 'has_audio',
      header: 'Audio',
      width: 60,
      render: (value, row) => (
        value ? (
          <PlayAudioButton text={row.question} title="Přehrát audio otázky" />
        ) : (
          <span style={{ fontSize: '1.1rem' }} title="Audio chybí">{'\u274C'}</span>
        )
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      width: 100,
      render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
  ];

  const editableFields = [
    { key: 'topic', type: 'select', label: 'Topic', required: true, options: uniqueTopics },
    { key: 'question', type: 'textarea', label: 'Question', minHeight: '60px', required: true },
    { key: 'hint', type: 'textarea', label: 'Hint', minHeight: '60px', required: true },
    { key: 'instruction', type: 'textarea', label: 'Instruction', minHeight: '40px' },
    grammarCategoriesField,
  ];

  if (loading && exercises.length === 0) {
    return <Loading />;
  }

  if (error && exercises.length === 0) {
    return <Error message={error} />;
  }

  return (
    <div>
      {selectedIds.length > 0 && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: '#1d4ed8', color: 'white', padding: '12px 24px',
          borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', gap: '16px', zIndex: 1000,
        }}>
          <span style={{ fontWeight: '600', fontSize: '14px' }}>{selectedIds.length} vybráno</span>
          <button onClick={handleGenerateAudio} disabled={generatingAudio}
            style={{ padding: '8px 16px', backgroundColor: 'white', color: '#0e7490', border: 'none', borderRadius: '8px', cursor: generatingAudio ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px' }}>
            {generatingAudio ? 'Generuji audio...' : 'Vygenerovat audio'}
          </button>
          <button onClick={() => setSelectedIds([])}
            style={{ padding: '8px 12px', backgroundColor: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
            Zrušit výběr
          </button>
        </div>
      )}

      {audioResult && (
        <div style={{
          marginBottom: '20px',
          padding: '12px 16px',
          borderRadius: '6px',
          backgroundColor: audioResult.success ? '#d4edda' : '#f8d7da',
          border: `1px solid ${audioResult.success ? '#c3e6cb' : '#f5c6cb'}`,
          color: audioResult.success ? '#155724' : '#721c24'
        }}>
          <p style={{ margin: 0, fontWeight: '500' }}>{audioResult.message}</p>
          {audioResult.errors && audioResult.errors.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '0.9rem' }}>
                Chyby ({audioResult.errors.length}):
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.85rem' }}>
                {audioResult.errors.slice(0, 5).map((err, index) => (
                  <li key={index}>
                    <strong>ID {err.exercise_id}</strong>: {err.error}
                  </li>
                ))}
                {audioResult.errors.length > 5 && (
                  <li>... a {audioResult.errors.length - 5} dalších chyb</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      <AdminTable
        data={exercises}
        columns={columns}
        onSave={handleSave}
        onDelete={handleDelete}
        editableFields={editableFields}
        title="Mluvení"
        filterField="topic"
        uniqueFilterValues={uniqueTopics}
        loading={loading}
        emptyMessage="No speaking exercises found."
        selectable={true}
        onSelectionChange={(ids) => setSelectedIds([...ids])}
      />
    </div>
  );
};

export default SpeakingExerciseManager;
